import { createQuery } from '@tanstack/svelte-query';
import { fromStore, get, toStore, type Writable } from 'svelte/store';
import {
	GetPrimaryCartDocument,
	type GetPrimaryCartQuery,
} from '../../queries/__generated__/getPrimaryCart.generated';
import { getPrimaryCartQueryVariables } from '../getPrimaryCartQueryVariables/getPrimaryCartQueryVariables';
import { FeatureFlags } from '#lib/constants/feature-flags.js';
import { NONEXISTENT_PRODUCT_ID } from '#lib/features/checkout/constants/nonexistent-product-id.js';
import type { CartEntryItem } from '#lib/features/checkout/types.js';
import { getQuery } from '#lib/query-utils/getQuery.js';
import { stringifyIds } from '#lib/websites/nft/utils/stringifyIds/stringifyIds.js';
import { getPrimaryCart } from '#lib/websites/primary/api/getPrimaryCart.js';
import { getQueryKeys } from '$global/query-utils/getQueryKeys';
import { getImageUrl } from '$global/utils/getImageUrl';

export const createPrimaryQuery = (
	items: Writable<{ productId: string; quantity: number }[]>,
	defaultLoading?: boolean
) => {
	const items$ = fromStore(items);

	const primaryQuery = createQuery(() => {
		const $items = items$.current;
		const ids = $items?.map(({ productId }) => productId);
		const queryIds = ids?.length ? ids : [NONEXISTENT_PRODUCT_ID];
		const variables = getPrimaryCartQueryVariables(queryIds);

		return FeatureFlags.RestPages['Primary cart and wishlist hydration']
			? {
					queryKey: getQueryKeys(GetPrimaryCartDocument, variables),
					queryFn: async (): Promise<GetPrimaryCartQuery> =>
						stringifyIds(await getPrimaryCart(fetch, queryIds)),
				}
			: getQuery(GetPrimaryCartDocument, variables);
	});

	return toStore(() => ({
		...primaryQuery,
		error: primaryQuery.error || null,
		data: primaryQuery.data || {
			Primary_Artworks: [],
			Primary_Generic_Artwork: [],
		},
		isLoading: (() => {
			if (!primaryQuery.data) {
				return typeof defaultLoading !== 'undefined' ? defaultLoading : false;
			}

			return primaryQuery.isPending;
		})(),
		formattedItems: [
			...(primaryQuery.data?.Primary_Generic_Artwork || []).map(
				(artwork): CartEntryItem => {
					const quantity = get(items).find(
						({ productId }) => artwork?.product?.id === productId
					)?.quantity;

					return {
						image: null,
						line1: artwork?.artists
							?.map((artist) => artist?.Artists_id?.name)
							?.join(', '),
						line2: artwork?.title,
						line3: artwork?.dimensions,
						productId: artwork?.product?.id,
						id: artwork?.id,
						quantity: quantity,
						currency: artwork?.product?.currency,
						sku: artwork?.product?.sku,
						price: artwork?.product?.price,
						variant: null,
						groupId: artwork?.collection?.code,
						groupName: artwork?.collection?.name,
						dropConfigId: artwork?.collection?.drop_page?.drop_config?.[0]?.id,
					};
				}
			),
			...(primaryQuery.data?.Primary_Artworks || []).map(
				(artwork): CartEntryItem => {
					const quantity = get(items).find(
						({ productId }) => artwork?.product?.id === productId
					)?.quantity;

					const allDefinedImage = artwork.artwork_featured_image
						? artwork.artwork_featured_image
						: null;

					return {
						image: allDefinedImage
							? {
									width: allDefinedImage?.width || 0,
									height: allDefinedImage?.height || 0,
									url: `${getImageUrl(allDefinedImage?.filename_disk, '512px')}`,
								}
							: null,
						line1: artwork?.artists
							?.map((artist) => artist?.Artists_id?.name)
							?.join(', '),
						line2: `${artwork.artwork_code || ''}${
							artwork.artwork_title
								? `${artwork.artwork_code ? '. ' : ''}${artwork.artwork_title}`
								: ''
						}`,
						line3: `${artwork?.primary_collection?.code} | ${artwork?.primary_collection?.name}`,
						line4:
							artwork?.artwork_dimensions ||
							`${artwork?.artwork_height_cm} x ${artwork?.artwork_width_cm} CM`,
						productId: artwork?.product?.id,
						id: artwork?.id,
						quantity: quantity,
						currency: artwork?.product?.currency,
						sku: artwork?.product?.sku,
						price: artwork?.product?.price,
						variant: null,
						// Some primary artworks might not have a series
						groupId: artwork?.primary_collection?.code || artwork?.artwork_code,
						groupName:
							artwork?.primary_collection?.name || artwork?.artwork_title,
						dropConfigId:
							artwork?.primary_collection?.drop_page?.drop_config?.[0]?.id,
					};
				}
			),
		],
	}));
};
